import { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { toast } from 'react-hot-toast'
import { Plus, Pencil, Trash2, Filter, Search, ChevronDown, ChevronUp, Loader2, Tag, Zap, Palette, Calendar, Settings, CreditCard, FileText, Stamp, Banknote, Ruler, LayoutGrid } from 'lucide-react'
import {
  Card,
  CardContent,
  Button,
  Input,
  Label,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Badge,
  Textarea,
  Separator,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  Switch,
} from '@jira-clone/ui'
import { useQuotationDefaults, useQuotationDefaultMutations } from '../../shared/hooks/useQuotationDefaults'
import { usePaymentTerms } from '../../shared/hooks/usePaymentTerms'
import { useAssumptionsExclusions } from '../../shared/hooks/useAssumptionsExclusions'
import { useTermConditions } from '../../shared/hooks/useTermConditions'
import { useDeclarations } from '../../shared/hooks/useDeclarations'
import { useBankDetails } from '../../shared/hooks/useBankDetails'
import { useQuotationDisplaySettings } from '../../shared/hooks/useQuotationDisplaySettings'
import { getApiErrorMessage } from '../../shared/utils/apiError'

interface DraftState {
  id?: string
  name: string
  defaultCurrency: string
  defaultValidityDays: number
  defaultPaymentTermId: string
  defaultGstEnabled: boolean
  defaultPlaceOfSupply: string
  defaultReverseCharge: boolean
  defaultDeclarationId: string
  defaultAssumptionsIds: string[]
  defaultExclusionsIds: string[]
  defaultTermsIds: string[]
  defaultDisplaySettingsId: string
  isActive: boolean
}

const EMPTY_DRAFT: DraftState = {
  name: '',
  defaultCurrency: 'INR',
  defaultValidityDays: 30,
  defaultPaymentTermId: '',
  defaultGstEnabled: true,
  defaultPlaceOfSupply: '',
  defaultReverseCharge: false,
  defaultDeclarationId: '',
  defaultAssumptionsIds: [],
  defaultExclusionsIds: [],
  defaultTermsIds: [],
  defaultDisplaySettingsId: '',
  isActive: true,
}

export default function QuotationDefaultsAdminPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [search, setSearch] = useState(searchParams.get('search') || '')
  const [isActiveFilter, setIsActiveFilter] = useState<'all' | 'true' | 'false'>(searchParams.get('isActive') as any || 'all')

  const handleSearchChange = (value: string) => {
    setSearch(value)
    setSearchParams(value ? { search: value } : {})
  }

  const handleIsActiveChange = (value: 'all' | 'true' | 'false') => {
    setIsActiveFilter(value)
    setSearchParams(value === 'all' ? {} : { isActive: value })
  }

  const { data: defaults = [], isLoading } = useQuotationDefaults({
    isActive: isActiveFilter === 'all' ? undefined : isActiveFilter === 'true',
    search: search || undefined,
  })
  
  const { create, update, setStatus, remove } = useQuotationDefaultMutations()
  
  // Fetch reference data for dropdowns
  const { data: paymentTerms = [] } = usePaymentTerms({ isActive: true })
  const { data: assumptions = [] } = useAssumptionsExclusions({ category: 'ASSUMPTION', isActive: true })
  const { data: exclusions = [] } = useAssumptionsExclusions({ category: 'EXCLUSION', isActive: true })
  const { data: terms = [] } = useTermConditions({ isActive: true })
  const { data: declarations = [] } = useDeclarations({ isActive: true })
  const { data: bankDetails = [] } = useBankDetails({ isActive: true })
  const { data: displaySettings = [] } = useQuotationDisplaySettings({ isActive: true })

  const [dialogOpen, setDialogOpen] = useState(false)
  const [draft, setDraft] = useState<DraftState>(EMPTY_DRAFT)
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<'general' | 'defaults' | 'references'>('general')

  const openCreate = () => {
    setDraft(EMPTY_DRAFT)
    setActiveTab('general')
    setDialogOpen(true)
  }

  const openEdit = (item: any) => {
    setDraft({
      id: item.id,
      name: item.name,
      defaultCurrency: item.defaultCurrency || 'INR',
      defaultValidityDays: item.defaultValidityDays || 30,
      defaultPaymentTermId: item.defaultPaymentTermId || '',
      defaultGstEnabled: item.defaultGstEnabled ?? true,
      defaultPlaceOfSupply: item.defaultPlaceOfSupply || '',
      defaultReverseCharge: item.defaultReverseCharge ?? false,
      defaultDeclarationId: item.defaultDeclarationId || '',
      defaultAssumptionsIds: item.defaultAssumptionsIds || [],
      defaultExclusionsIds: item.defaultExclusionsIds || [],
      defaultTermsIds: item.defaultTermsIds || [],
      defaultDisplaySettingsId: item.defaultDisplaySettingsId || '',
      isActive: item.isActive,
    })
    setActiveTab('general')
    setDialogOpen(true)
  }

  const handleSave = async () => {
    if (!draft.name.trim()) {
      toast.error('Name is required.')
      return
    }
    try {
      const payload = {
        name: draft.name.trim(),
        defaultCurrency: draft.defaultCurrency,
        defaultValidityDays: draft.defaultValidityDays,
        defaultPaymentTermId: draft.defaultPaymentTermId || null,
        defaultGstEnabled: draft.defaultGstEnabled,
        defaultPlaceOfSupply: draft.defaultPlaceOfSupply || null,
        defaultReverseCharge: draft.defaultReverseCharge,
        defaultDeclarationId: draft.defaultDeclarationId || null,
        defaultAssumptionsIds: draft.defaultAssumptionsIds,
        defaultExclusionsIds: draft.defaultExclusionsIds,
        defaultTermsIds: draft.defaultTermsIds,
        defaultDisplaySettingsId: draft.defaultDisplaySettingsId || null,
        isActive: draft.isActive,
      }
      if (draft.id) {
        await update.mutateAsync({ id: draft.id, payload })
        toast.success('Quotation defaults updated.')
      } else {
        await create.mutateAsync(payload)
        toast.success('Quotation defaults created.')
      }
      setDialogOpen(false)
    } catch (err) {
      toast.error(getApiErrorMessage(err, draft.id ? 'update quotation defaults' : 'create quotation defaults'))
    }
  }

  const handleDelete = async (item: any) => {
    if (!confirm(`Delete "${item.name}"? This can't be undone.`)) return
    setDeletingId(item.id)
    try {
      await remove.mutateAsync(item.id)
      toast.success('Quotation defaults deleted.')
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'delete quotation defaults'))
    } finally {
      setDeletingId(null)
    }
  }

  const handleToggleActive = async (item: any) => {
    try {
      await setStatus.mutateAsync({ id: item.id, isActive: !item.isActive })
      toast.success(item.isActive ? 'Defaults deactivated.' : 'Defaults activated.')
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'toggle defaults status'))
    }
  }

  const handleMultiSelectChange = (field: keyof DraftState, value: string, checked: boolean) => {
    const current = draft[field] as string[]
    if (checked) {
      setDraft({ ...draft, [field]: [...current, value] })
    } else {
      setDraft({ ...draft, [field]: current.filter((v) => v !== value) })
    }
  }

  return (
    <div className="space-y-4 max-w-4xl">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-foreground">Quotation Defaults</h2>
          <p className="text-sm text-muted-foreground">Manage default values for quotation creation.</p>
        </div>
        <Button onClick={openCreate} className="gap-1.5">
          <Plus className="h-4 w-4" /> New Defaults Profile
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            {/* Search */}
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search defaults profiles..."
                value={search}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="pl-9"
              />
            </div>

            {/* Active Filter */}
            <Select value={isActiveFilter} onValueChange={handleIsActiveChange}>
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="true">Active</SelectItem>
                <SelectItem value="false">Inactive</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : defaults.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-sm text-muted-foreground">
              No quotation defaults yet. Create the first one.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {defaults.map((item) => (
            <Card key={item.id}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <Settings className="h-5 w-5 text-muted-foreground" />
                      <Badge variant={item.isActive ? 'default' : 'secondary'} className="text-xs">
                        {item.isActive ? 'Active' : 'Inactive'}
                      </Badge>
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-semibold text-foreground truncate">{item.name}</p>
                      </div>
                      <p className="text-xs text-muted-foreground truncate flex items-center gap-2">
                        <span>Currency: {item.defaultCurrency || 'INR'}</span>
                        <span>Validity: {item.defaultValidityDays || 30} days</span>
                        <span>GST: {item.defaultGstEnabled ? 'Enabled' : 'Disabled'}</span>
                        {item.defaultPaymentTerm && <span>Term: {item.defaultPaymentTerm.name}</span>}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button variant="ghost" size="icon" onClick={() => openEdit(item)} title="Edit">
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleToggleActive(item)}
                      title={item.isActive ? 'Deactivate' : 'Activate'}
                    >
                      {item.isActive ? (
                        <span className="h-3.5 w-3.5 rounded-full bg-green-500" />
                      ) : (
                        <span className="h-3.5 w-3.5 rounded-full bg-gray-400" />
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(item)}
                      disabled={deletingId === item.id}
                      title="Delete"
                    >
                      <Trash2 className="h-3.5 w-3.5 text-destructive" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle>{draft.id ? 'Edit Quotation Defaults' : 'New Quotation Defaults Profile'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2 max-h-[80vh] overflow-y-auto">
            <div className="flex border-b border-border">
              {[
                { key: 'general', label: 'General', icon: Settings },
                { key: 'defaults', label: 'Defaults', icon: CreditCard },
                { key: 'references', label: 'References', icon: FileText },
              ].map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key as any)}
                  className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                    activeTab === tab.key
                      ? 'border-primary text-primary font-semibold'
                      : 'border-transparent text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <tab.icon className="h-4 w-4" />
                  {tab.label}
                </button>
              ))}
            </div>

            {activeTab === 'general' && (
              <div className="space-y-4 mt-4 p-4">
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label htmlFor="qd-name">Profile Name *</Label>
                    <Input
                      id="qd-name"
                      value={draft.name}
                      onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
                      placeholder="e.g. Standard Quotation Defaults"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="qd-currency">Default Currency</Label>
                    <Select value={draft.defaultCurrency} onValueChange={(v) => setDraft((d) => ({ ...d, defaultCurrency: v }))}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="INR">INR (₹)</SelectItem>
                        <SelectItem value="USD">USD ($)</SelectItem>
                        <SelectItem value="EUR">EUR (€)</SelectItem>
                        <SelectItem value="GBP">GBP (£)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="qd-validity">Default Validity (days)</Label>
                    <Input
                      id="qd-validity"
                      type="number"
                      min={1}
                      max={365}
                      value={draft.defaultValidityDays}
                      onChange={(e) => setDraft((d) => ({ ...d, defaultValidityDays: parseInt(e.target.value) || 30 }))}
                      placeholder="30"
                    />
                  </div>
                </div>

                <Separator className="my-2" />

                <h4 className="text-sm font-medium text-foreground">GST Settings</h4>
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="flex items-center gap-2">
                    <input
                      id="qd-gst"
                      type="checkbox"
                      checked={draft.defaultGstEnabled}
                      onChange={(e) => setDraft((d) => ({ ...d, defaultGstEnabled: e.target.checked }))}
                    />
                    <label htmlFor="qd-gst" className="text-sm cursor-pointer">Enable GST by default</label>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      id="qd-rc"
                      type="checkbox"
                      checked={draft.defaultReverseCharge}
                      onChange={(e) => setDraft((d) => ({ ...d, defaultReverseCharge: e.target.checked }))}
                    />
                    <label htmlFor="qd-rc" className="text-sm cursor-pointer">Reverse Charge by default</label>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'defaults' && (
              <div className="space-y-4 mt-4 p-4">
                <h4 className="text-sm font-medium text-foreground">Default Selections</h4>
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label htmlFor="qd-payment-term">Default Payment Term</Label>
                    <Select value={draft.defaultPaymentTermId} onValueChange={(v) => setDraft((d) => ({ ...d, defaultPaymentTermId: v }))}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select payment term" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="">None</SelectItem>
                        {paymentTerms.map((pt) => (
                          <SelectItem key={pt.id} value={pt.id}>
                            {pt.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="qd-display-settings">Default Display Settings</Label>
                    <Select value={draft.defaultDisplaySettingsId} onValueChange={(v) => setDraft((d) => ({ ...d, defaultDisplaySettingsId: v }))}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select display settings" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="">None</SelectItem>
                        {displaySettings.map((ds) => (
                          <SelectItem key={ds.id} value={ds.id}>
                            {ds.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <Separator className="my-2" />

                <h4 className="text-sm font-medium text-foreground">Default Declaration</h4>
                <div className="space-y-1.5">
                  <Label htmlFor="qd-declaration">Default Declaration</Label>
                  <Select value={draft.defaultDeclarationId} onValueChange={(v) => setDraft((d) => ({ ...d, defaultDeclarationId: v }))}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select declaration" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="">None</SelectItem>
                      {declarations.map((d) => (
                        <SelectItem key={d.id} value={d.id}>
                          {d.title}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            {activeTab === 'references' && (
              <div className="space-y-4 mt-4 p-4">
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label>Default Assumptions</Label>
                    <div className="space-y-1 max-h-40 overflow-y-auto border border-border rounded-lg p-2">
                      {assumptions.map((a) => (
                        <label key={a.id} className="flex items-center gap-2 text-sm cursor-pointer">
                          <input
                            type="checkbox"
                            checked={draft.defaultAssumptionsIds.includes(a.id)}
                            onChange={(e) => handleMultiSelectChange('defaultAssumptionsIds', a.id, e.target.checked)}
                          />
                          <span>{a.title}</span>
                        </label>
                      ))}
                      {assumptions.length === 0 && <p className="text-xs text-muted-foreground">No assumptions available</p>}
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Default Exclusions</Label>
                    <div className="space-y-1 max-h-40 overflow-y-auto border border-border rounded-lg p-2">
                      {exclusions.map((excl) => (
                        <label key={excl.id} className="flex items-center gap-2 text-sm cursor-pointer">
                          <input
                            type="checkbox"
                            checked={draft.defaultExclusionsIds.includes(excl.id)}
                            onChange={(ev) => handleMultiSelectChange('defaultExclusionsIds', excl.id, ev.target.checked)}
                          />
                          <span>{excl.title}</span>
                        </label>
                      ))}
                      {exclusions.length === 0 && <p className="text-xs text-muted-foreground">No exclusions available</p>}
                    </div>
                  </div>
                  <div className="space-y-1.5 md:col-span-2">
                    <Label>Default Terms & Conditions</Label>
                    <div className="space-y-1 max-h-40 overflow-y-auto border border-border rounded-lg p-2">
                      {terms.map((t) => (
                        <label key={t.id} className="flex items-center gap-2 text-sm cursor-pointer">
                          <input
                            type="checkbox"
                            checked={draft.defaultTermsIds.includes(t.id)}
                            onChange={(e) => handleMultiSelectChange('defaultTermsIds', t.id, e.target.checked)}
                          />
                          <span>{t.title}</span>
                        </label>
                      ))}
                      {terms.length === 0 && <p className="text-xs text-muted-foreground">No terms available</p>}
                    </div>
                  </div>
                </div>
              </div>
            )}

            <Separator className="my-2" />

            <div className="flex items-center gap-2">
              <Label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={draft.isActive}
                  onChange={(e) => setDraft((d) => ({ ...d, isActive: e.target.checked }))}
                />
                <span>Active</span>
              </Label>
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={create.isPending || update.isPending}>
              {draft.id ? 'Save Changes' : 'Create Defaults Profile'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}